import React from 'react';
import useSWRInfinite from 'swr/infinite';
import { useMatchMutate } from '../../../utils/swr/swr';
import { BrandTemplate, MarketingPaginatedResponse } from '../../../providers/api/dtos';
import { searchBrandTemplates } from '../../../providers/api/marketing/marketingProvider';

export const SEARCH_BRAND_TEMPLATES_RID = 'search-brand-templates';

interface UseSearchBrandTemplatesInfiniteResult {
  data: BrandTemplate[],
  isLoading: boolean,
  isLoadingMore: boolean,
  hasMore: boolean,
  error: Error | undefined,
  loadMore: () => void,
  mutate: () => void,
}

export const useSearchBrandTemplatesInfinite = (query?: string): UseSearchBrandTemplatesInfiniteResult => {
  const matchMutate = useMatchMutate();

  const getKey = (pageIndex: number, previousPage: MarketingPaginatedResponse<BrandTemplate> | null) => {
    if (previousPage && !previousPage.continuation) return null;

    const continuation = previousPage?.continuation ?? null;
    return [SEARCH_BRAND_TEMPLATES_RID, query ?? '', continuation];
  };

  const result = useSWRInfinite(
    getKey,
    ([, , continuation]) => searchBrandTemplates(query, { continuation: continuation ?? undefined }),
  );

  const allItems = result.data?.flatMap((page) => page.content) ?? [];
  const isLoadingMore = result.isValidating && (result.data?.length ?? 0) > 0;
  const lastPage = result.data?.at(-1);

  const loadMore = React.useCallback(() => {
    if (lastPage?.continuation) {
      result.setSize((s) => s + 1);
    }
  }, [lastPage?.continuation, result]);

  return {
    data: allItems,
    isLoading: result.isLoading,
    isLoadingMore,
    error: result.error,
    hasMore: !!lastPage?.continuation,
    loadMore,
    mutate: () => matchMutate(SEARCH_BRAND_TEMPLATES_RID),
  };
};
